import { Component, computed, inject, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import type { ResolveFn, Routes } from '@angular/router'
import { ActivatedRoute, RedirectCommand, Router } from '@angular/router'

import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { of } from 'rxjs'
import { catchError, map, switchMap } from 'rxjs/operators'

import type { PageDto } from '../../api/responses'
import { PagesService } from '../../services/pages/pages.service'
import { LocalizedDatePipe } from '../../shared/pipes/localized-date.pipe'
import { SeoService } from '../../shared/seo/seo.service'
import type { SeoPageMeta, SeoRouteMeta } from '../../shared/seo/seo.types'
import { firstImage, firstText } from '../../shared/seo/seo.util'
import { lexicalJsonToHtml } from '../../shared/utils/lexical-to-html.util'

const PAGE_LOCALES = ['nl', 'en'] as const
type PageLocale = (typeof PAGE_LOCALES)[number]

function toPageLocale(lang: string | undefined): PageLocale {
  return PAGE_LOCALES.includes(lang as PageLocale) ? (lang as PageLocale) : 'nl'
}

// Fetching in a resolver (rather than the component's ngOnInit) matters for
// SSR: the router only considers navigation complete once resolvers emit, so
// this is guaranteed to be awaited before the page is rendered/captured. A
// fetch started from ngOnInit races the server's render-and-destroy cycle and
// can be torn down mid-request before it ever emits a value.
export const pageResolver: ResolveFn<PageDto | null> = route => {
  const pagesService = inject(PagesService)
  const translateService = inject(TranslateService)
  const router = inject(Router)

  // This route matches any otherwise-unmatched single URL segment, ahead of
  // the app's catch-all wildcard - so an unknown slug (typo, removed CMS
  // page, failed fetch) has to redirect itself rather than rendering an
  // empty 200 page.
  const notFound = (): RedirectCommand =>
    new RedirectCommand(router.createUrlTree(['/']))

  const slug = route.paramMap.get('slug')
  if (!slug) return of(notFound())

  const locale = toPageLocale(translateService.getCurrentLang() ?? undefined)
  return pagesService.getPageBySlug(slug, locale).pipe(
    map(page => page ?? notFound()),
    catchError((error: unknown) => {
      console.error('Error fetching page:', error)
      return of(notFound())
    })
  )
}

/**
 * Head metadata for a CMS page, from the `meta` group the API's `seoPlugin`
 * maintains. `meta.title` is often left empty by editors, in which case the
 * page's own title is the better fallback than the site default.
 */
export function pageSeoMeta(page: PageDto | null): SeoPageMeta | null {
  if (!page) return null

  return {
    title: firstText(page.meta?.title, page.title),
    description: firstText(page.meta?.description),
    image: firstImage(page.meta?.image),
    canonicalPath: `/${page.slug}`,
  }
}

/** Registered as `data: { seo: pageSeo }`; see seo-title.strategy.ts. */
export const pageSeo: SeoRouteMeta = route =>
  pageSeoMeta((route.data['page'] as PageDto | null | undefined) ?? null)

@Component({
  selector: 'dhp-page',
  templateUrl: './page.component.html',
  styleUrl: './page.component.scss',
  imports: [TranslatePipe, LocalizedDatePipe],
})
export class PageComponent {
  private readonly route = inject(ActivatedRoute)
  private readonly pagesService = inject(PagesService)
  private readonly translateService = inject(TranslateService)
  private readonly router = inject(Router)
  private readonly seoService = inject(SeoService)

  readonly page = signal<PageDto | null>(null)
  readonly contentHtml = computed(() =>
    lexicalJsonToHtml(this.page()?.content, this.router.url)
  )

  constructor() {
    // Resolved per navigation (including a slug change while this component
    // is reused), and already available synchronously during SSR.
    this.route.data
      .pipe(
        map(data => (data['page'] as PageDto | null) ?? null),
        takeUntilDestroyed()
      )
      .subscribe(page => {
        this.page.set(page)
      })

    // The resolver only reruns on navigation, so a language switch without a
    // slug change needs its own refetch to keep the content in sync.
    this.translateService.onLangChange
      .pipe(
        switchMap(event => {
          const slug = this.route.snapshot.paramMap.get('slug')
          if (!slug) return of(null)

          return this.pagesService
            .getPageBySlug(slug, toPageLocale(event.lang))
            .pipe(catchError(() => of(null)))
        }),
        takeUntilDestroyed()
      )
      .subscribe(page => {
        this.page.set(page)
        // A language switch refetches without navigating, so the title
        // strategy doesn't run and the head would keep the old language.
        this.seoService.apply(pageSeoMeta(page))
      })
  }
}

export const PageRoutes: Routes = [
  {
    path: ':slug',
    component: PageComponent,
    resolve: { page: pageResolver },
    data: { seo: pageSeo },
  },
]
